/**
 * What's My Size - After Five Studio
 *
 * SINGLE SOURCE OF TRUTH: Google Sheet → whats-my-size-data.js
 *   - window.__AF_SIZE_CHARTS  = brand size charts (measurements)
 *   - window.__AF_PRODUCTS     = per-product info (brand + size & fit text)
 *
 * Product handle comes from the page (data-wms-handle attribute).
 * The tool looks up the product handle → gets brand → gets that brand's chart.
 * Size+Fit text also comes from the sheet data (not Shopify metafield).
 *
 * Only recommends sizes that exist as actual product variants.
 * Saves measurements to localStorage for cross-product recommendations.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'afterfive_my_measurements';

  /* ── helpers ── */
  function cmToIn(v) { return +(v / 2.54).toFixed(1); }
  function inToCm(v) { return +(v * 2.54).toFixed(1); }

  function parseRange(str) {
    if (!str || str === '-') return null;
    str = String(str).replace(/\s/g, '');
    if (str.charAt(0) === '<') {
      var maxVal = parseFloat(str.substring(1));
      return isNaN(maxVal) ? null : { min: 0, max: maxVal };
    }
    var parts = str.split('-');
    if (parts.length === 2) {
      var a = parseFloat(parts[0]), b = parseFloat(parts[1]);
      if (!isNaN(a) && !isNaN(b)) return { min: a, max: b };
    }
    var n = parseFloat(str);
    return isNaN(n) ? null : { min: n - 2, max: n + 2 };
  }

  function getSaved() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch (e) { return null; }
  }
  function saveMeasurements(data) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) {}
  }

  /* ── get chart data from unified sheet data ── */
  function getChartData() {
    if (!window.__AF_PRODUCTS || !window.__AF_SIZE_CHARTS) return null;

    // Get product handle from page
    var handleEl = document.querySelector('[data-wms-handle]');
    if (!handleEl) return null;
    var handle = handleEl.getAttribute('data-wms-handle').trim();

    var productInfo = window.__AF_PRODUCTS[handle];
    if (!productInfo || !productInfo.brand) return null;

    var brand = productInfo.brand.toUpperCase().trim();
    return window.__AF_SIZE_CHARTS[brand] || null;
  }

  function getProductInfo() {
    if (!window.__AF_PRODUCTS) return null;
    var handleEl = document.querySelector('[data-wms-handle]');
    if (!handleEl) return null;
    var handle = handleEl.getAttribute('data-wms-handle').trim();
    return window.__AF_PRODUCTS[handle] || null;
  }

  function getAvailableSizes() {
    var el = document.querySelector('[data-wms-available-sizes]');
    if (el) {
      var raw = el.getAttribute('data-wms-available-sizes');
      if (raw) return raw.split(',').map(function (s) { return s.trim().toUpperCase(); });
    }
    return null;
  }

  function filterToAvailable(chartSizes, availableSizes) {
    if (!availableSizes || !availableSizes.length) return chartSizes;
    return chartSizes.filter(function (s) {
      return availableSizes.indexOf(s.label.trim().toUpperCase()) !== -1;
    });
  }

  /* ── recommendation engine ── */
  function recommend(sizes, bustCm, waistCm, hipsCm) {
    if (!sizes || !sizes.length) return null;
    var best = null, bestScore = Infinity;

    for (var i = 0; i < sizes.length; i++) {
      var s = sizes[i], score = 0, measurements = 0;
      var checks = [
        { val: bustCm, range: parseRange(s.bust) },
        { val: waistCm, range: parseRange(s.waist) },
        { val: hipsCm, range: parseRange(s.hips) }
      ];
      for (var c = 0; c < checks.length; c++) {
        if (checks[c].val && checks[c].range) {
          measurements++;
          var mid = (checks[c].range.min + checks[c].range.max) / 2;
          var diff = Math.abs(checks[c].val - mid);
          if (checks[c].val < checks[c].range.min) diff += (checks[c].range.min - checks[c].val) * 2;
          else if (checks[c].val > checks[c].range.max) diff += (checks[c].val - checks[c].range.max) * 2;
          score += diff;
        }
      }
      if (measurements > 0) {
        score = score / measurements;
        if (score < bestScore) { bestScore = score; best = { label: s.label, score: score, index: i }; }
      }
    }
    return best;
  }

  /* ── parse fit summary from Size+Fit text ── */
  function parseFitSummary(text) {
    if (!text) return '';
    var t = text.toLowerCase();

    if (t.indexOf('true to size') !== -1) {
      if (t.indexOf('small fit') !== -1 || t.indexOf('runs small') !== -1) {
        return 'Runs small - we recommend sizing up';
      }
      return 'True to size';
    }

    if (t.indexOf('must size up') !== -1 || t.indexOf('runs small') !== -1 || t.indexOf('size up') !== -1 || t.indexOf('recommend sizing up') !== -1 || t.indexOf('small fit') !== -1) return 'Runs small - we recommend sizing up';
    if (t.indexOf('runs large') !== -1 || t.indexOf('size down') !== -1 || t.indexOf('recommend sizing down') !== -1) return 'Runs large - we recommend sizing down';
    return '';
  }

  /* ── apply fit adjustment — bumps result up/down one size if needed ── */
  function applyFitAdjustment(result, sizes, sizeFitText) {
    if (!result || !sizes || !sizes.length) return result;
    var summary = parseFitSummary(sizeFitText);
    var direction = 0;
    if (summary === 'Runs small - we recommend sizing up') direction = 1;
    else if (summary === 'Runs large - we recommend sizing down') direction = -1;
    if (direction === 0) return result;

    var newIndex = result.index + direction;
    if (newIndex < 0 || newIndex >= sizes.length) return result;
    return { label: sizes[newIndex].label, score: result.score, index: newIndex, adjusted: true };
  }

  /* ── build modal ── */
  function buildModal(sizeFitText) {
    var overlay = document.createElement('div');
    overlay.className = 'wms-overlay';
    overlay.id = 'wms-overlay';

    var fitSummary = parseFitSummary(sizeFitText);
    var sizeFitHtml = fitSummary ? '<p class="wms-sizefit-note">' + fitSummary + '</p>' : '';

    var modal = document.createElement('div');
    modal.className = 'wms-modal';
    modal.innerHTML = [
      '<button type="button" class="wms-close" id="wms-close" aria-label="Close">',
      '  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
      '</button>',
      '<h2 class="wms-title">What\'s My Size?</h2>',
      '',
      '<!-- Unit toggle -->',
      '<div class="wms-unit-toggle" id="wms-unit-toggle">',
      '  <button type="button" class="active" data-unit="cm">CM</button>',
      '  <button type="button" data-unit="in">INCHES</button>',
      '</div>',
      '',
      '<!-- Input Form -->',
      '<div id="wms-form-section">',
      '  <div class="wms-fields">',
      '    <div class="wms-field">',
      '      <label for="wms-bust">Bust (<span class="wms-unit-label">cm</span>)</label>',
      '      <input type="number" id="wms-bust" placeholder="e.g. 86" step="0.5" min="50" max="200" inputmode="decimal">',
      '    </div>',
      '    <div class="wms-field">',
      '      <label for="wms-waist">Waist (<span class="wms-unit-label">cm</span>)</label>',
      '      <input type="number" id="wms-waist" placeholder="e.g. 68" step="0.5" min="40" max="180" inputmode="decimal">',
      '    </div>',
      '    <div class="wms-field">',
      '      <label for="wms-hips">Hips (<span class="wms-unit-label">cm</span>)</label>',
      '      <input type="number" id="wms-hips" placeholder="e.g. 92" step="0.5" min="50" max="200" inputmode="decimal">',
      '    </div>',
      '  </div>',
      sizeFitHtml,
      '  <button type="button" class="wms-submit" id="wms-submit">Find My Size</button>',
      '</div>',
      '',
      '<!-- Result -->',
      '<div class="wms-result" id="wms-result">',
      '  <p class="wms-result-answer">Your size is <strong id="wms-result-size">M</strong></p>',
      '  <div class="wms-result-actions">',
      '    <button type="button" class="wms-btn-select" id="wms-select-size">Select This Size</button>',
      '    <button type="button" class="wms-btn-reset" id="wms-remeasure">Update Measurements</button>',
      '  </div>',
      '</div>'
    ].join('\n');

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    return overlay;
  }

  /* ── select size on page ── */
  function selectSizeOnPage(sizeLabel) {
    var upper = sizeLabel.trim().toUpperCase();
    var radios = document.querySelectorAll('.product-form__input input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      if (radios[r].value.trim().toUpperCase() === upper) { radios[r].click(); return; }
    }
    var selects = document.querySelectorAll('.product-form__input select');
    for (var s = 0; s < selects.length; s++) {
      for (var o = 0; o < selects[s].options.length; o++) {
        if (selects[s].options[o].value.trim().toUpperCase() === upper) {
          selects[s].value = selects[s].options[o].value;
          selects[s].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }
  }

  /* ── main init ── */
  function init() {
    var rawSizes = getChartData();
    if (!rawSizes) return;

    var availableSizes = getAvailableSizes();
    var sizes = filterToAvailable(rawSizes, availableSizes);
    if (!sizes.length) return;

    // Get size+fit from sheet data (not metafield)
    var productInfo = getProductInfo();
    var sizeFitText = productInfo ? (productInfo.sizeFit || '') : '';

    // Insert just above the buy button
    var buyBtnWrap = document.querySelector('.product-form__buttons');
    if (!buyBtnWrap) return;

    var container = document.createElement('div');
    container.className = 'wms-above-buy';
    container.id = 'wms-container';

    var link = document.createElement('button');
    link.type = 'button';
    link.className = 'wms-link';
    link.id = 'wms-trigger';
    link.innerHTML = '<svg class="wms-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/></svg> <span class="wms-link-text">What is my size?</span>';

    var recSpan = document.createElement('span');
    recSpan.className = 'wms-recommendation';
    recSpan.id = 'wms-rec';
    recSpan.style.display = 'none';

    container.appendChild(link);
    container.appendChild(recSpan);

    buyBtnWrap.parentNode.insertBefore(container, buyBtnWrap);

    var overlay = null;
    var currentUnit = 'cm';

    function ensureModal() {
      if (!overlay) {
        overlay = buildModal(sizeFitText);
        bindEvents();
      }
    }

    function bindEvents() {
      document.getElementById('wms-close').addEventListener('click', closeModal);
      overlay.addEventListener('click', function (e) { if (e.target === overlay) closeModal(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && overlay && overlay.classList.contains('active')) closeModal();
      });

      document.getElementById('wms-unit-toggle').addEventListener('click', function (e) {
        var btn = e.target.closest('button');
        if (!btn) return;
        var unit = btn.dataset.unit;
        if (unit === currentUnit) return;

        this.querySelectorAll('button').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');

        var fields = ['wms-bust', 'wms-waist', 'wms-hips'];
        for (var k = 0; k < fields.length; k++) {
          var inp = document.getElementById(fields[k]);
          if (inp && inp.value) {
            var val = parseFloat(inp.value);
            inp.value = (unit === 'in' ? cmToIn(val) : inToCm(val)).toFixed(1);
          }
        }

        document.querySelectorAll('.wms-unit-label').forEach(function (el) {
          el.textContent = unit === 'in' ? 'in' : 'cm';
        });

        var ph = unit === 'in'
          ? { bust: 'e.g. 34', waist: 'e.g. 27', hips: 'e.g. 36' }
          : { bust: 'e.g. 86', waist: 'e.g. 68', hips: 'e.g. 92' };
        document.getElementById('wms-bust').placeholder = ph.bust;
        document.getElementById('wms-waist').placeholder = ph.waist;
        document.getElementById('wms-hips').placeholder = ph.hips;

        currentUnit = unit;
      });

      document.getElementById('wms-submit').addEventListener('click', function () {
        var bustVal = parseFloat(document.getElementById('wms-bust').value);
        var waistVal = parseFloat(document.getElementById('wms-waist').value);
        var hipsVal = parseFloat(document.getElementById('wms-hips').value);

        if (isNaN(bustVal) && isNaN(waistVal) && isNaN(hipsVal)) {
          alert('Please enter at least one measurement (bust, waist, or hips)');
          return;
        }

        var bustCm = !isNaN(bustVal) ? (currentUnit === 'in' ? inToCm(bustVal) : bustVal) : 0;
        var waistCm = !isNaN(waistVal) ? (currentUnit === 'in' ? inToCm(waistVal) : waistVal) : 0;
        var hipsCm = !isNaN(hipsVal) ? (currentUnit === 'in' ? inToCm(hipsVal) : hipsVal) : 0;

        var result = recommend(sizes, bustCm || 0, waistCm || 0, hipsCm || 0);
        if (!result) {
          alert('We couldn\'t determine your size. Please check your measurements.');
          return;
        }
        result = applyFitAdjustment(result, sizes, sizeFitText);

        saveMeasurements({
          bust: bustCm || null,
          waist: waistCm || null,
          hips: hipsCm || null,
          preferredUnit: currentUnit,
          savedAt: new Date().toISOString()
        });

        showResult(result);
        showRecommendation(result.label);
      });

      document.getElementById('wms-remeasure').addEventListener('click', function () {
        document.getElementById('wms-result').classList.remove('active');
        document.getElementById('wms-form-section').style.display = '';
      });

      document.getElementById('wms-select-size').addEventListener('click', function () {
        var sizeLabel = document.getElementById('wms-result-size').textContent;
        selectSizeOnPage(sizeLabel);
        closeModal();
      });
    }

    function showResult(result) {
      document.getElementById('wms-form-section').style.display = 'none';
      document.getElementById('wms-result-size').textContent = result.label;
      document.getElementById('wms-result').classList.add('active');
    }

    function showRecommendation(sizeLabel) {
      var rec = document.getElementById('wms-rec');
      if (rec) {
        rec.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:14px;height:14px"><polyline points="20 6 9 17 4 12"/></svg> We recommend: <strong>' + sizeLabel + '</strong>';
        rec.style.display = '';
      }
    }

    function openModal() {
      ensureModal();

      var saved = getSaved();
      if (saved) {
        currentUnit = saved.preferredUnit || 'cm';

        var fields = { 'wms-bust': saved.bust, 'wms-waist': saved.waist, 'wms-hips': saved.hips };
        for (var fid in fields) {
          var inp = document.getElementById(fid);
          if (inp && fields[fid]) {
            inp.value = currentUnit === 'in' ? cmToIn(fields[fid]).toFixed(1) : fields[fid];
          }
        }

        document.querySelectorAll('#wms-unit-toggle button').forEach(function (b) {
          b.classList.toggle('active', b.dataset.unit === currentUnit);
        });
        document.querySelectorAll('.wms-unit-label').forEach(function (el) {
          el.textContent = currentUnit === 'in' ? 'in' : 'cm';
        });
        if (currentUnit === 'in') {
          document.getElementById('wms-bust').placeholder = 'e.g. 34';
          document.getElementById('wms-waist').placeholder = 'e.g. 27';
          document.getElementById('wms-hips').placeholder = 'e.g. 36';
        }

        var bustCm = saved.bust || 0, waistCm = saved.waist || 0, hipsCm = saved.hips || 0;
        if (bustCm || waistCm || hipsCm) {
          var result = recommend(sizes, bustCm, waistCm, hipsCm);
          if (result) {
            result = applyFitAdjustment(result, sizes, sizeFitText);
            showResult(result);
            showRecommendation(result.label);
          }
        }
      } else {
        document.getElementById('wms-form-section').style.display = '';
        document.getElementById('wms-result').classList.remove('active');
        ['wms-bust', 'wms-waist', 'wms-hips'].forEach(function (id) {
          var el = document.getElementById(id);
          if (el) el.value = '';
        });
      }

      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }

    document.getElementById('wms-trigger').addEventListener('click', openModal);

    // Auto-recommend on load if saved
    var saved = getSaved();
    if (saved && (saved.bust || saved.waist || saved.hips)) {
      var result = recommend(sizes, saved.bust || 0, saved.waist || 0, saved.hips || 0);
      if (result) {
        result = applyFitAdjustment(result, sizes, sizeFitText);
        var rec = document.getElementById('wms-rec');
        if (rec) {
          rec.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:14px;height:14px"><polyline points="20 6 9 17 4 12"/></svg> We recommend: <strong>' + result.label + '</strong>';
          rec.style.display = '';
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 150);
  }
})();
